// Tuning tables. Numbers come from the Godot build's data/ and GAMEPLAY_LOOP.md,
// adapted where the browser slice simplifies a system (noted inline).

import { secs } from './core';

export const enum ClassId { Gunslinger = 0, Valkyrie = 1, Phantom = 2, Juggernaut = 3 }
export const CLASS_COUNT = 4;

/** Armor-pen tiers, 0-indexed as in the Godot engine (AP1..AP5 in UI copy). Ordnance skips a value on purpose
 *  (damage_packet.gd): it strictly beats AV4 metal, where anti-tank only matches it. */
export const enum AP { Light = 0, Medium = 1, Heavy = 2, AntiTank = 3, Ordnance = 5 }
// ---- armour (Godot health.gd armor_multiplier_for; Nick 2026-10-21): a round above its target's armour does full
// damage, AT its tier AP_EQUAL_MUL, under it AP_CHIP_MUL chip and the round RICOCHETS: it bounces off, slower,
// and whatever it hits next (a robot, a cowboy, you) takes RICOCHET_FRAC of the gun's damage at the gun's AP
export const AP_EQUAL_MUL = 0.65;
export const AP_CHIP_MUL = 0.01;
/** a round into a robot's back (its forward · the round's direction above this) is a weak-point hit: armour is bypassed */
export const WEAK_POINT_DOT = 0.5;
export const RICOCHET_FRAC = 0.1;
export const RICOCHET_RANGE = 22; // Godot: x0.55 speed for 0.9 s more
export const RICOCHET_UP = 0.25;
export const RICOCHET_JITTER = 0.2;
/** splash (a blast's area, not its direct hit) lands at one tier less (Godot player.gd area damage AP - 1) */
export const SPLASH_AP_DROP = 1;

export interface WeaponDef {
  name: string;
  damage: number;
  pellets: number;
  interval: number; // ticks between shots
  auto: boolean;
  mag: number;
  reloadTicks: number;
  reloadHeat: number;
  /** per-round reload (Big Iron, shotguns): heat + time are per round */
  rounds: boolean;
  spreadDeg: number;
  ap: AP;
  range: number;
  headMul: number;
}

export interface ClassDef {
  name: string;
  role: string;
  weapon: WeaponDef;
  shieldMax: number;
  shieldRegen: number; // per second (Godot shield_refill_rate); every point costs SHIELD_REGEN_HEAT
  heatMax: number;
  heatCool: number; // per second after grace (Godot heat_regen_rate_override)
  coolDelay: number; // seconds after the last heat added before passive cooling starts (Godot cool grace)
  hoverHeat: number; // per second while hovering (Godot CRUISE_BASE x heat_build_mul x hover_cost_mul)
  flightHeat: number; // per second in traversal flight (Godot CRUISE_BASE 5.5 x flight_heat_build_mul)
  climbHeat: number; // extra heat/s at a full nose-up climb (Godot K_CLIMB 27.5 x flight_climb_cost_mul), x climb^1.5
  turnRate: number; // traversal turn responsiveness (rad/s)
  thrust: number; // peak flight thrust m/s^2 (Godot A_THRUST 30.5 / class mass); decel scales with it too
  hoverLift: number; // m/s^2 of thrust while hovering
  primer: string;
  detonator: string;
  flavor: string;
  ability: string;
  abilityCd: number; // ticks
  abilityHeat: number;
  chainTicks: number;
  scarf: number; // default scarf hue index (player slot overrides)
}

const W = (w: Partial<WeaponDef> & Pick<WeaponDef, 'name' | 'damage'>): WeaponDef => ({
  pellets: 1, interval: secs(0.2), auto: false, mag: 12, reloadTicks: secs(1.4), reloadHeat: 20,
  rounds: false, spreadDeg: 1.5, ap: AP.Medium, range: 160, headMul: 2, ...w,
});

export const CLASSES: ClassDef[] = [
  {
    name: 'Gunslinger', role: 'Marksman',
    weapon: W({ name: 'Big Iron', damage: 55, interval: secs(0.42), mag: 6, reloadTicks: secs(0.45), reloadHeat: 5.2, rounds: true, spreadDeg: 0.5, ap: AP.AntiTank, headMul: 2.2 }),
    shieldMax: 85, shieldRegen: 10, heatMax: 110, heatCool: 12, coolDelay: 1.2, hoverHeat: 2.48, flightHeat: 5.5, climbHeat: 27.5, turnRate: 2.97, thrust: 30.5, hoverLift: 25,
    primer: 'PISTOL WHIP', detonator: 'HEADSHOT', flavor: 'OVERLOAD BURST',
    ability: '', abilityCd: 0, abilityHeat: 0, chainTicks: secs(3), scarf: 0,
  },
  {
    name: 'Valkyrie', role: 'Stormcaller',
    weapon: W({ name: 'Pulse Rifle', damage: 12, interval: secs(0.095), auto: true, mag: 38, reloadTicks: secs(1.6), reloadHeat: 26, spreadDeg: 1.8, ap: AP.Medium }),
    shieldMax: 25, shieldRegen: 9, heatMax: 85, heatCool: 10, coolDelay: 1.0, hoverHeat: 1.82, flightHeat: 3.03, climbHeat: 15.1, turnRate: 3.4, thrust: 33.9, hoverLift: 26,
    primer: 'FIRE DASH', detonator: 'SHOOT FROM THE AIR', flavor: 'ELEMENT STORM',
    ability: 'FIRE DASH', abilityCd: secs(3), abilityHeat: 6, chainTicks: secs(2.5), scarf: 1, // 3 charges, each back in 3 s (VDASH_*)
  },
  {
    name: 'Phantom', role: 'Duelist',
    weapon: W({ name: 'Whisper', damage: 30, interval: secs(0.26), mag: 10, reloadTicks: secs(1.2), reloadHeat: 14, spreadDeg: 0.7, ap: AP.Heavy, headMul: 2.5 }),
    shieldMax: 50, shieldRegen: 15, heatMax: 120, heatCool: 18, coolDelay: 0.6, hoverHeat: 3.47, flightHeat: 5.78, climbHeat: 27.5, turnRate: 3.26, thrust: 32.1, hoverLift: 25,
    primer: 'BACKSTAB', detonator: 'SHOOT FROM BEHIND', flavor: 'CHAIN FRACTURE',
    ability: 'WARP', abilityCd: secs(0.3), abilityHeat: 40, chainTicks: secs(1.5), scarf: 2, // Godot: 40 heat, 0.3 s (WARP_*)
  },
  {
    name: 'Juggernaut', role: 'Siege Tank',
    weapon: W({ name: 'Auto MG', damage: 9, interval: secs(0.075), auto: true, mag: 75, reloadTicks: secs(2.4), reloadHeat: 40, spreadDeg: 3.2, ap: AP.Medium, range: 120 }),
    shieldMax: 150, shieldRegen: 12, heatMax: 145, heatCool: 9, coolDelay: 1.4, hoverHeat: 6.78, flightHeat: 7.98, climbHeat: 39.9, turnRate: 2.53, thrust: 25.4, hoverLift: 23,
    primer: 'SHIELD BASH', detonator: 'CHARGE', flavor: 'STAGGER QUAKE',
    ability: 'CHARGE', abilityCd: secs(4), abilityHeat: 10, chainTicks: secs(4), scarf: 3,
  },
];

// ---------------------------------------------------------------- class verbs (GAMEPLAY_LOOP §1.2)
/** Phantom: the warp no longer cloaks (Nick 2026-10-21, James approved: Godot's loop starts from the blink
 *  strike below, and only a detonation cloaks you). Kept as a knob. */
export const WARP_CLOAK = 0;
// ---- Phantom (Godot player.gd _perform_warp / _commit_phantom_blink)
export const WARP_DIST = 6; // a true teleport along the camera, pulled back from walls and edges
export const WARP_HEAT = 40; // warp_dash_cost 20 x2
export const WARP_CD = secs(0.3);
export const WARP_IFRAMES = secs(0.25);
/** the reflection window after a warp: an enemy bolt that hits you primes its shooter */
export const WARP_REFLECT = secs(1.0);
/** robots the warp passed through can be backstabbed from any angle for this long */
export const WARP_GRACE = secs(3);
/** the warp sweep: robots this close to the line are clipped aside (force WARP_CLIP_FORCE / weight) */
export const WARP_CLIP_R = 1.3;
export const WARP_CLIP_FORCE = 18;
/** warping on a hot pack hurts (bypasses the shield): heat fraction -> HP */
export const WARP_SELF_DMG: [number, number][] = [[0.7, 10], [0.6, 5], [0.5, 1]];
/** hold R3 this long: the blink strike. The nearest robot in a BLINK_CONE cone within BLINK_RANGE (x BLINK_HOVER_MUL
 *  from a hover), you land 1 m past it facing back, it's primed, stunned and everything near is shoved */
export const BLINK_HOLD = secs(0.25);
export const BLINK_RANGE = 4.5;
export const BLINK_HOVER_MUL = 1.8;
export const BLINK_CONE = 0.819; // cos 35 deg
export const BLINK_HEAT = 10; // free from a hover
export const BLINK_DMG = 12;
export const BLINK_STUN = secs(1);
export const BLINK_KNOCK_R = 4;
export const BLINK_KNOCK = 13;
export const BLINK_CD = secs(0.6);
export const BLINK_IFRAMES = secs(0.12);
/** the Phantom's tap-R3 jab (Godot 8; the web's 45 backstab was a liberty) */
export const PHANTOM_JAB = 8;
// ---- Valkyrie (Godot ELEMENTAL_DASH_* and the ground pound)
export const VDASH_CHARGES = 3;
export const VDASH_RECHARGE = secs(3);
export const VDASH_GAP = secs(0.25);
export const VDASH_HEAT = 6;
export const VDASH_TICKS = secs(0.4);
export const VDASH_SPEED = 16.2;
export const VDASH_IFRAMES = secs(0.35);
/** R3 in the air: a straight-down slam that burns the whole shield; damage and radius grow with the height fallen */
export const POUND_FALL = 35;
export const POUND_DMG = 30;
export const POUND_R0 = 3;
export const POUND_R1 = 6;
export const POUND_H = 8;
export const POUND_HEAT = 5;
// ---- Juggernaut (Godot shield bash tap / charged hammer hold)
export const BASH_TAP = secs(0.15); // R3 let go inside this: the bash; held longer: the hammer winds up
export const HAMMER_WINDUP = secs(0.3); // rooted from here
export const HAMMER_MAX = secs(1.0); // full charge (auto-release)
export const HAMMER_DMG = 40; // x (1 + charge)
export const HAMMER_RANGE = 3;
export const HAMMER_CONE = 100; // degrees, +10 at full charge
export const HAMMER_CD = secs(1.5);
export const HAMMER_HEAT = 5;
/** Phantom: Smoke and Mirrors, the cloak a detonation opens (Godot's playtested 2.5 s, up from the doc's 1.5) */
export const DETONATE_CLOAK = secs(2.5);
/** Juggernaut: a Charge burns your WHOLE current shield (Godot `_consume_player_shield(current_shield)`) and
 *  banks every point into the special weapon and grenades; its damage scales with how much it burnt
 *  (Godot: 40 x 1.25 x shield ratio). Charging on an empty shield is allowed and weak. */
export const CHARGE_DMG_BASE = 15; // the shove with no shield behind it
export const CHARGE_DMG_FULL = 50; // a full shield's worth (Godot's 50)
export const CHARGE_DET_BASE = 20; // detonation on a primed robot, no shield
export const CHARGE_DET_FULL = 50;

// ---------------------------------------------------------------- shields (Godot player.gd "Shield regen")
/** seconds of no damage (or self-spent shield) before the shield starts coming back: 7 s per Godot's playtest
 *  pass ("forces a proper disengage"); the Juggernaut's is 25 % shorter on every recovery */
export const SHIELD_DELAY = secs(7);
export const JUGG_SHIELD_DELAY_MUL = 0.75;
/** heat burnt per shield point regenerated, 1:1 for every class. Regen pauses if the next tick would push the
 *  pack into the red (SHIELD_REGEN_HEAT_CEIL of heatMax), so healing the shield and flying compete for one tank */
export const SHIELD_REGEN_HEAT = 1;
export const SHIELD_REGEN_HEAT_CEIL = 0.8;
export const shieldDelayFor = (cls: ClassId) => Math.round(SHIELD_DELAY * (cls === ClassId.Juggernaut ? JUGG_SHIELD_DELAY_MUL : 1));

// ---------------------------------------------------------------- the pack on fire (Godot player.gd "Fire state", GAMEPLAY_LOOP §1.5)
/** HP/s in the 80-99 % band (bypasses the shield: it's on your back) */
export const HEAT_RED_DPS = 8;
/** HP/s once the pack catches fire at 100 %: the lockout (no jets, reload or class move) until it goes out */
export const HEAT_FIRE_DPS = 20;
/** a burning pack keeps feeding itself heat, so it won't drop below the red band on its own */
export const HEAT_FIRE_SELF = 4;
/** the band's floor: below it the mild burn stops and a pack fire goes out */
export const HEAT_RED = 0.8;
// flight never reaches the fireball: at this fraction the engine SPUTTERS OUT instead (flight_heat.gd FLIGHT_CEILING01)
export const FLIGHT_CEIL = 0.995;
/** after a flameout: dead time with no venting, then the pack vents hard until it relights at RELIGHT of heatMax */
export const FLAMEOUT_DELAY = secs(0.9);
export const FLAMEOUT_VENT = 34; // heat/s, on top of passive cooling
export const RELIGHT = 0.4;
/** Valkyrie: Sky-Stalker, primes she lays from the air inside her chain window last this many times longer */
export const SKY_PRIME_MUL = 2;

// ---------------------------------------------------------------- weight (Godot health.gd / enemy.gd / player.gd knockback)
/** a staggered robot reels this long and takes STAGGER_DMG_MUL x damage (Godot stagger_duration / stagger_damage_multiplier) */
export const STAGGER_SECS = 2;
export const STAGGER_DMG_MUL = 2;
/** stagger force by source, against EnemyDef.staggerRes (Godot: melee 150, slide 200) */
export const STAGGER_MELEE = 150;
export const STAGGER_GRENADE = 180;
export const STAGGER_ORDNANCE = 260;
export const STAGGER_CHARGE = 220;
/** every firearm round stamps a micro-flinch: the robot freezes in place for these ticks (Godot micro_stun 0.025 s) */
export const FLINCH_TICKS = 2;
/** a cowboy's knockback, as a speed (Godot force x 0.6151): any damaging hit stuns input for HIT_STUN; past
 *  KNOCK_FLIGHT it breaks flight and hover; past KNOCK_DOWN it floors you (KNOCKDOWN on the ground, then GETUP
 *  of invulnerable get-up, no second floor inside KNOCK_CD); past KNOCK_RAGDOLL the client ragdolls the body */
export const HIT_STUN = secs(0.139);
/** playtest 2 (Nick): every hit stuns a beat scaled by its share of the hull (robots and cowboys), and shoves; the caps */
export const HIT_STUN_PER_FRAC = secs(0.6), HIT_STUN_MAX = secs(0.45), HIT_SHOVE = 4, ROBOT_HIT_STUN_MAX = secs(0.35);
export const KNOCK_FLIGHT = 11;
export const KNOCK_DOWN = 12.3;
export const KNOCK_RAGDOLL = 21.5;
export const KNOCKDOWN = secs(0.7);
export const GETUP = secs(0.45);
export const KNOCK_CD = secs(1.5);
/** ground friction while a shove plays out (the hit stun coasts; normal GROUND_FRICTION would kill it in 0.2 s) */
export const KNOCK_FRICTION = 16;
/** what a knocked cowboy's velocity is capped at (Godot caps incoming force at 150) */
export const KNOCK_MAX = 40;
export const enum KnockKind { Down = 1, Ragdoll = 2 }

// ---------------------------------------------------------------- enemies
export const enum EType { Swarmer = 0, Shooter = 1, Brute = 2, Sniper = 3, Bomber = 4, MetalBrute = 5, Disruptor = 6, Engineer = 7, Cerberus = 8, Bulwark = 9, Angler = 10,
  /** THE BLACK COURT's pieces (sim/bosses/court.ts), in piece order: keep them contiguous */
  CourtKing = 11, CourtQueen = 12, CourtRook = 13, CourtBishop = 14, CourtKnight = 15, CourtPawn = 16,
  /** THE LEVIATHAN, the extraction pursuer (sim/bosses/leviathan.ts) */
  Leviathan = 17,
  /** the Stalker (Godot stalker.gd): a cloaked ambusher, past the boss block (the renderer draws it on the Swarmer kit) */
  Stalker = 18,
  /** the Engineer's auto-turret (engineer.gd): a planted gun, drawn small on the Engineer kit */
  Turret = 19,
  /** the fliers (flying_shooter.gd, flying_sniper.gd, support_helicopter.gd): hover at altitude, rest now and then */
  FlyingShooter = 20, FlyingSniper = 21, Helicopter = 22 }

/** robot types drawn by the instanced robot view; boss bodies (Cerberus = 8, Bulwark = 9...) sit past it
 *  and belong to a boss in sim/bosses (its own view, hit volumes and HUD bar) */
export const ETYPE_COUNT = 8;
export const isBossType = (t: number) => t >= 8 && t <= 17;

export interface EnemyDef {
  name: string;
  hp: number;
  shield: number;
  armor: number; // AV: 0 light .. 4 metal (only AP5 ordnance beats it; AP4 anti-tank matches it)
  radius: number; // body sphere
  bodyY: number; // body centre height above feet
  headY: number;
  headR: number;
  speed: number;
  weight: number; // knockback resistance
  /** stagger resistance (Godot Health.stagger_resistance): a hit whose stagger force beats it reels the robot
   *  for STAGGER_SECS and doubles the damage it takes meanwhile. Bosses are unstaggerable. */
  staggerRes: number;
  cost: number; // director budget
  /** fog cap etc. are client-side; this is pure rules */
}

export const ENEMIES: EnemyDef[] = [
  { name: 'Swarmer', hp: 40, shield: 0, armor: 0, radius: 0.55, bodyY: 0.55, headY: 0.95, headR: 0.28, speed: 6.4, weight: 1, staggerRes: 30, cost: 1 },
  { name: 'Shooter', hp: 55, shield: 25, armor: 0, radius: 0.75, bodyY: 1.0, headY: 1.65, headR: 0.32, speed: 3.6, weight: 1.6, staggerRes: 50, cost: 2 },
  { name: 'Brute', hp: 140, shield: 0, armor: 2, radius: 1.1, bodyY: 1.3, headY: 2.45, headR: 0.42, speed: 3.4, weight: 4, staggerRes: 200, cost: 4 },
  { name: 'Sniper', hp: 30, shield: 0, armor: 0, radius: 0.6, bodyY: 1.0, headY: 1.6, headR: 0.28, speed: 4.2, weight: 1.2, staggerRes: 20, cost: 2 },
  { name: 'Bomber', hp: 35, shield: 20, armor: 0, radius: 0.85, bodyY: 1.0, headY: 1.75, headR: 0.3, speed: 3.2, weight: 2, staggerRes: 35, cost: 3 },
  { name: 'Metal Brute', hp: 900, shield: 0, armor: 4 /* Godot apply_metal_armor: AV4 */, radius: 1.8, bodyY: 2.1, headY: 3.9, headR: 0.62, speed: 3.0, weight: 12, staggerRes: 250, cost: 20 },
  // Support units (disruptor.gd / engineer.gd): Godot hp 25 → 30 like the sniper/bomber port,
  // shields 30 / 20, MEDIUM armor (AV 1), knockback mass 1.5. Hit volumes match the renderer's
  // models (client/render/robots), so what you see is what you hit.
  { name: 'Disruptor', hp: 30, shield: 30, armor: 1, radius: 0.7, bodyY: 1.6, headY: 2.0, headR: 0.36, speed: 3.0, weight: 1.5, staggerRes: 80, cost: 3 },
  { name: 'Engineer', hp: 30, shield: 20, armor: 1, radius: 0.9, bodyY: 1.8, headY: 1.95, headR: 0.4, speed: 3.5, weight: 1.5, staggerRes: 80, cost: 3 },
  // The boss island's boss (sim/boss.ts). Hits resolve against its part spheres, not these;
  // radius/bodyY/headY are what generic code (melee reach, separation, the camera) sees.
  // hp is the solo pool; sim/run.ts scales it per posse member.
  { name: 'Cerberus', hp: 2600, shield: 0, armor: 3, radius: 4.2, bodyY: 7.6, headY: 14, headR: 2.6, speed: 2.6, weight: 200, staggerRes: 99999, cost: 40 },
  // A soldier of THE BULWARK CUBE (sim/bosses/bulwark.ts). Shots resolve against its slab / cabinet /
  // backsplash boxes; these spheres are for blasts, melee and specials (armor 1: a blast still bites).
  { name: 'Bulwark', hp: 70, shield: 0, armor: 1, radius: 0.75, bodyY: 1.0, headY: 2.0, headR: 0.45, speed: 4.5, weight: 999, staggerRes: 99999, cost: 3 },
  // THE ANGLER (sim/bosses/angler.ts). Shots resolve against its eye / lure / drum / plating boxes;
  // these spheres are for blasts, melee and specials (armor 4: the AP5 chassis, blasts chip).
  { name: 'Angler', hp: 3000, shield: 0, armor: 4, radius: 2.3, bodyY: 4.6, headY: 7.0, headR: 2.2, speed: 6, weight: 200, staggerRes: 99999, cost: 40 },
  // THE BLACK COURT (sim/bosses/court.ts): shots resolve against each piece's GLB part boxes; these
  // spheres are for blasts, melee and specials. The King's chassis is AP5 (only his back core bites).
  { name: 'Court King', hp: 1500, shield: 0, armor: 4, radius: 1.4, bodyY: 2.2, headY: 4.3, headR: 0.9, speed: 3.2, weight: 999, staggerRes: 99999, cost: 20 },
  { name: 'Court Queen', hp: 420, shield: 0, armor: 2, radius: 1.0, bodyY: 1.5, headY: 2.9, headR: 0.65, speed: 3, weight: 999, staggerRes: 99999, cost: 6 },
  { name: 'Court Rook', hp: 260, shield: 0, armor: 2, radius: 1.05, bodyY: 1.3, headY: 2.4, headR: 0.7, speed: 3, weight: 999, staggerRes: 99999, cost: 4 },
  { name: 'Court Bishop', hp: 130, shield: 0, armor: 1, radius: 0.8, bodyY: 1.4, headY: 1.6, headR: 0.4, speed: 3, weight: 999, staggerRes: 99999, cost: 3 },
  { name: 'Court Knight', hp: 110, shield: 0, armor: 1, radius: 0.6, bodyY: 1.1, headY: 2.1, headR: 0.4, speed: 3, weight: 999, staggerRes: 99999, cost: 3 },
  { name: 'Court Pawn', hp: 60, shield: 0, armor: 0, radius: 0.75, bodyY: 1.0, headY: 1.6, headR: 0.45, speed: 3, weight: 999, staggerRes: 99999, cost: 1 },
  // THE LEVIATHAN, the extraction pursuer (sim/bosses/leviathan.ts): one body for the whole creature.
  // Shots resolve against its part volumes; radius ~ the scaled hull so seekers and melee meet its skin.
  // hp is replaced on summon by the run-long wound (it carries across extractions).
  { name: 'Leviathan', hp: 12000, shield: 0, armor: 4, radius: 40, bodyY: 0, headY: 30, headR: 8, speed: 0, weight: 99999, staggerRes: 99999, cost: 0 },
  // the Stalker (Godot stalker.gd _init: hp 30, no shield, stagger 40, mass 1.2): cloaked until it strikes or is hit
  { name: 'Stalker', hp: 30, shield: 0, armor: 0, radius: 0.6, bodyY: 0.75, headY: 1.1, headR: 0.28, speed: 5.0, weight: 1.2, staggerRes: 40, cost: 3 },
  // the Engineer's turret (engineer.gd: 30 hull, 3 a shot at 1.4 s, 20 m): planted, never moves
  { name: 'Turret', hp: 30, shield: 0, armor: 0, radius: 0.45, bodyY: 0.5, headY: 0.8, headR: 0.2, speed: 0, weight: 99, staggerRes: 99999, cost: 0 },
  // the fliers (FLYERS in ROSTER.md): a Shooter and a Sniper that hover (6 m / 8 m), and the Support Helicopter (12 m) that
  // wards the biggest robot, hoses the ground wet and heals what is under it
  { name: 'Flying Shooter', hp: 55, shield: 25, armor: 0, radius: 0.75, bodyY: 1.0, headY: 1.65, headR: 0.32, speed: 4.5, weight: 1.6, staggerRes: 50, cost: 4 },
  { name: 'Flying Sniper', hp: 30, shield: 0, armor: 0, radius: 0.6, bodyY: 1.0, headY: 1.6, headR: 0.28, speed: 4.5, weight: 1.2, staggerRes: 20, cost: 4 },
  { name: 'Support Helicopter', hp: 90, shield: 0, armor: 1, radius: 1.1, bodyY: 1.2, headY: 2.0, headR: 0.4, speed: 6.5, weight: 4, staggerRes: 150, cost: 6 },
];
/** model scale of Assets/bosses/cerberus/cerberus.glb in the world (about 18 m to the top of the heads) */
export const CERBERUS_SCALE = 0.82;

/** Disruptor (disruptor.gd): support aura + elemental grenades, three rolling tactics. */
export const DISRUPTOR = {
  auraR: 6, auraHeal: 3, auraShield: 3, auraWoundedFrac: 0.75,
  range: 14, rearguardExtra: 6,
  grenadeCd: 8, zonerMul: 0.5, rearguardMul: 1.6,
  shepherdSearchR: 16,
  tacticSecs: 10, tacticJitter: 2.5,
} as const;
export const enum DTactic { Shepherd = 0, Zoner = 1, Rearguard = 2 }

/** Engineer (engineer.gd): gas canisters + field-medic repair beam. Turrets are not ported. */
export const ENGINEER = {
  range: 16, turtleRange: 9, fleeSecs: 2,
  gasCd: 10, gasTrapperMul: 0.5, gasDps: 8, gasSecs: 5, gasR: 4, gasFlight: 1.1,
  repairRange: 10, repairPerSec: 6, repairSearchR: 18, scanSecs: 0.4,
  tacticSecs: 10, tacticJitter: 2.5,
} as const;
/** FORWARD_WALL is turret-only in Godot, so the port rolls the other three. */
export const enum ETactic { TurtleRepair = 0, GasTrapper = 1, FieldMedic = 2 }

/** Player status effects (CLAUDE.md gotcha 11, 2026-07-22 ruling). */
export const ICE_SLOW_PER_STACK = 0.1;
export const ICE_SLOW_MAX_STACKS = 3;
export const ICE_STACK_DECAY_SECS = 3;
export const MAGNETIC_SHIELD_BLOCK_SECS = 10;
export const GAS_SLOW_MUL = 0.75;

export const PLAYER_RADIUS = 0.45;
export const PLAYER_HEIGHT = 1.8;
export const EYE_HEIGHT = 1.55;
export const HULL_MAX = 100;
export const POSSE_LIVES = 6;

// ---- elite affixes (Godot enemy.gd EliteAffix, encounter_manager.gd ELITE_AFFIX_*): one extra defence on a mid-tier robot
export const enum Affix { None = 0, /** the AV4 metal gate */ Ironclad = 1, /** a 120 shield that refills whole after 4 s unhit */ Aegis = 2, /** the hyper shield: only a prime → detonate breaks it */ Vaultlock = 3, /** a shell only melee breaks */ Redshell = 4, /** a Support Helicopter projects its shield onto it: shoot the helicopter first */ Warded = 5 }
export const AFFIX_NAME = ['', 'IRONCLAD', 'AEGIS', 'VAULTLOCK', 'REDSHELL', 'WARDED'];
export const WARDED_ELIGIBLE: EType[] = [EType.Brute];
export const AFFIX_CHANCE = [0, 0, 0.15, 0.3, 0.5]; // by village
export const AFFIX_MAX_PER_ISLAND = 2;
export const AFFIX_HP_MUL = 1.3, AEGIS_SHIELD = 120, AEGIS_DELAY_SECS = 4, REDSHELL_MUL = 0.1;
/** the robots an affix can land on; Redshell only on ranged kits, Vaultlock never on a Shooter (Godot playtest ruling) */
export const AFFIX_ELIGIBLE: EType[] = [EType.Brute, EType.Shooter, EType.Sniper, EType.Stalker, EType.Bomber, EType.Engineer, EType.Disruptor];
export const REDSHELL_ELIGIBLE: EType[] = [EType.Shooter, EType.Sniper, EType.Engineer, EType.Bomber];
export const VAULTLOCK_ELIGIBLE: EType[] = [EType.Brute, EType.Sniper, EType.Stalker, EType.Bomber, EType.Engineer, EType.Disruptor];
/** the Stalker's numbers (stalker.gd): stalk / retreat speed, reach, bite, its stagger and shove, the cooldown, the reveal */
export const STALK_SPEED = 5, STALK_RETREAT = 7, STALK_REACH = 1.8, STALK_DMG = 25, STALK_STAGGER = 80, STALK_KNOCK = 12, STALK_CD = 2, STALK_REVEAL = 1.5;
export const STALK_TACTIC_SECS = 10, STALK_TACTIC_JITTER = 2.5, STALK_ORBIT = 3, STALK_REAR_COS = -0.25, STALK_PATIENCE = 4, STALK_FADE_MUL = 1.6;
/** the Swarm Captain (swarm_captain.gd): a big Swarmer that births a brood; its chaff panics when it falls */
export const CAPTAIN_HP = 100, CAPTAIN_SPAWN_SECS = 8, CAPTAIN_BATCH = 3, CAPTAIN_RING = 1.8, CAPTAIN_SCALE = 1.6;
/** morale (ENEMY_AI_DESIGN §3): when a wave's last anchor falls its chaff routs: it flees for ROUT_SECS and sinks away, loot-free */
export const ROUT_SECS = 6;

// ---- run mutators (Godot run_mutators.gd, ENEMY_AI_DESIGN §5): one or two a run, rolled from the seed, each a multiplier at one choke point
export const enum Mut { Unbreakable = 0, Zealous = 1, Bloodthirsty = 2, Brittle = 3, Scrapheap = 4, Overbuilt = 5, Silent = 6, Frenzied = 7, Mirror = 8 }
export const MUT_COUNT = 9;
export const MUT_NAME = ['UNBREAKABLE', 'ZEALOUS', 'BLOODTHIRSTY', 'BRITTLE', 'SCRAPHEAP', 'OVERBUILT', 'SILENT', 'FRENZIED', 'MIRROR'];
export const MUT_FLAVOR = [
  'THE ROBOTS DO NOT FLINCH AND DO NOT RUN', 'THE ROBOTS DO NOT HESITATE AND NEVER FLEE', 'THE ROBOTS HIT 1.25X HARDER', 'THE ROBOTS TAKE 1.25X: A FREEBIE',
  'EVERY KILL POPS A BURST INTO THE ROBOTS AROUND IT', 'HEAVIES CARRY 1.3X PLATING; A THIRD LESS CHAFF', 'TELEGRAPHS RUN 0.75X AS LONG', 'ROBOT COOLDOWNS RUN 0.8X', 'ELEMENTAL ROBOTS WEAR THE POSSE\'S FAVOURITE ELEMENT',
];
export const MUT_BLOOD = 1.25, MUT_BRITTLE = 1.25, MUT_SCRAP_DMG = 10, MUT_SCRAP_R = 2.5, MUT_OVERBUILT_HULL = 1.3, MUT_OVERBUILT_CHAFF = 0.7, MUT_SILENT = 0.75, MUT_FRENZIED = 0.8;

// ---- dismantling (Godot weapon_sever.gd, panel_shed.gd, engineer.gd turrets; DISMANTLE in ROSTER.md)
/** the gun router on a Shooter / Sniper: its own pool, a quarter of what it takes forwarded to the body; destroyed = disarmed */
export const SEVER_HP = 30, SEVER_FORWARD = 0.25, SEVER_R = 0.36;
/** a Brute sheds a panel to a detonation on it or a blast of BLAST_SHED_DMG+ touching it: a tier of armour and a wider back weak arc each */
export const SHED_MAX = 2, SHED_CD_SECS = 1, SHED_BLAST_DMG = 20;
/** the turret: deploy cooldown, fire rate, reach, damage (engineer.gd) */
export const TURRET_DEPLOY_SECS = 12, TURRET_FIRE_SECS = 1.4, TURRET_RANGE = 20, TURRET_DMG = 3, TURRET_LIFE_SECS = 60;
/** elemental barrels (elemental_barrel.gd): a grenade you can place in the world; a cluster's size is its element level */
export const BARREL_HP = 12, BARREL_R = 4, BARREL_DMG = 50, BARREL_FUSE_TICKS = 6, BARREL_RADIUS = 0.5;

// ---- the fliers (flying_shooter.gd, flying_sniper.gd, support_helicopter.gd)
export const HOVER_ALT: Record<number, number> = { [EType.FlyingShooter]: 6, [EType.FlyingSniper]: 8, [EType.Helicopter]: 12 };
export const HOVER_CLIMB = 2, HOVER_THRUST = 30, REST_IN_MIN = 8, REST_IN_MAX = 16, REST_MIN = 2, REST_MAX = 3, REST_DESCENT = 5;
export const HELI_ORBIT = 8, HELI_SHIELD = 60, HELI_SHIELD_REGEN = 35, HELI_WARD_RANGE = 40, HELI_SPRAY_SECS = 4, HELI_SPRAY_DUR = 1.4, HELI_SPRAY_R = 14, HELI_SPRAY_COOL = 25, HELI_HEAL_R = 20, HELI_HEAL = 4;
export const isFlier = (t: number) => t === EType.FlyingShooter || t === EType.FlyingSniper || t === EType.Helicopter;

// ---- visible damage (playtest 3, Nick): a robot's parts take the hits that land on them and come off while it lives
/** parts: 0 the top (head accessory), 1 the right arm, 2 the left arm, 3 the legs; a part comes off after this share of the hull */
export const enum Part { Top = 0, RightArm = 1, LeftArm = 2, Legs = 3 }
export const PART_POP_FRAC = 0.35;
export const LIMP_MUL = 0.55;
